import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { Toast, useToast } from '@zillow/constellation-10';

import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useRentalManagerUserPropertyEditV2WebApiHandler_clearPropertyOnly,
  useRentalManagerUserPropertyEditV2WebApiHandler_displayTotalMonthlyPriceWithFee,
  useRentalManagerUserPropertyEditV2WebApiHandler_modularListingProgress,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { ERROR_PAGE_TYPES } from '@zg-rentals/zillow-rental-manager/constants';
import { formatAddress } from '@zg-rentals/zillow-rental-manager/utils';

import { buildWizardContext } from '../../components/CreateListingPage/utils/flowResolver';
import { isAiOptOutStep, resolveInitialStep } from '../../components/CreateListingPage/utils/stepHelpers';
import { AiDraftGenerationProvider } from '../../contexts/AiDraftGenerationContext';
import { TourSchedulingProvider } from '../../contexts/TourSchedulingContext';
import { useMultiplexListingFlow } from '../../hooks';
import { useMultiplexStepPrefetch } from '../../hooks/queries/multiplex';
import { useGetListingDetails } from '../../hooks/queries/properties';
import { useAiGeneratedFields } from '../../hooks/queries/useAiGeneratedFields';
import { useAiListingCreationTestBAccess } from '../../hooks/useAIListingCreation';
import { useOwnershipVerificationAccess } from '../../hooks/useOwnershipVerificationAccess';
import { useSyndicationUpdates } from '../../hooks/useSyndicationUpdates';
import accessUtils from '../../utils/accessUtils';
import { getListingsReturnRoute, getPortfolioOrigin, PORTFOLIO_ORIGIN } from '../../utils/navigationCacheUtils';

import {
  AI_OPT_OUT_STEP_SUFFIX,
  RELISTING_STEP_SUFFIX,
  STEP_DELIMITER,
  STEP_KEYS,
} from '../../components/CreateListingPage/constants/stepKeys';
import { PROGRESS_STEP_KEYS } from '../../components/CreateListingPage/constants/stepKeys';
import { getActiveProgressGroups, getActiveSteps } from '../../components/CreateListingPage/constants/stepRegistry';
import { STEP_DIRECTION } from '../../components/WizardStepController/constants';
import { getDashboardOverviewRoute, ROUTES } from '../../constants/urls';
import { EXIT_CTA_LABEL } from '../../constants/WizardConstants';

import { useAuth } from '../../components/AuthProvider';
import AiDraftFailureGate from '../../components/CreateListingPage/components/AiDraftFailureGate';
import { CLPLoadingState } from '../../components/CreateListingPage/LoadingState';
import { FlowType } from '../../components/CreateListingPage/types';
import {
  type GoToStepOptions,
  WizardNavigationProvider,
} from '../../components/CreateListingPage/WizardNavigationProvider';
import ErrorPage from '../../components/ErrorPage';
import WizardStepController from '../../components/WizardStepController';

import { emitContinueFromEvent, emitNextEvent, emitSaveAndExitEvent } from '../../analytics/events/modularListing';
import { emitActiveListingViewEvent, emitOffMarketListingViewEvent } from '../../analytics/events/reviewPage';
import { useListingInitialDefaults } from './useListingInitialDefaults';

import { type TourConfirmationType } from '@zg-rentals/ts-z-calendar-client';
import { type DatarouterError } from '@zg-rentals/web-base';

type Props = {
  isNewProperty?: boolean;
};

export default function CreateListingPage({ isNewProperty = false }: Props) {
  const router = useRouter();
  const { user, isAuthorized } = useAuth();
  const queryClient = useQueryClient();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;

  const {
    data: listingDetailsData,
    isLoading: isLoadingProperty,
    error: listingDetailsError,
  } = useGetListingDetails(listingAlias, {
    enabled: isAuthorized,
  });

  useSyndicationUpdates({ listingAlias });

  const [selectedBookingMethodForTourScheduling, setSelectedBookingMethodForTourScheduling] = useState<
    TourConfirmationType | undefined
  >(undefined);

  const { isLoading: isMultiplexListingFlowLoading, isInMultiplexListingFlow } = useMultiplexListingFlow();
  const multiplexBuildingSessionId = router.query.multiplexBuildingSessionId as string | undefined;

  const { mutateAsync: saveWizardProgress, error: modularListingProgressError } =
    useRentalManagerUserPropertyEditV2WebApiHandler_modularListingProgress();

  const { mutateAsync: setDisplayTotalMonthlyPriceWithFee } =
    useRentalManagerUserPropertyEditV2WebApiHandler_displayTotalMonthlyPriceWithFee();

  const { mutate: clearPropertyOnly } = useRentalManagerUserPropertyEditV2WebApiHandler_clearPropertyOnly({
    meta: { context: { api: 'clearPropertyOnly', details: 'CreateListingPage' } },
  });

  useEffect(() => {
    if (!listingDetailsData?.isPropertyOnly) return;
    clearPropertyOnly({ postData: { listingAlias, multiplexBuildingSessionId } });
  }, [listingDetailsData?.isPropertyOnly, clearPropertyOnly, listingAlias, multiplexBuildingSessionId]);

  const isFeed = listingDetailsData?.isFeed ?? false;
  const active = listingDetailsData?.active ?? false;
  const lastActivatedMs = listingDetailsData?.lastActivatedMs;
  const isMultifamily = listingDetailsData?.isMultifamily ?? false;

  const canAccessMLReactivationFlow =
    isInMultiplexListingFlow || !listingDetailsData
      ? false
      : accessUtils.canAccessMLReactivationFlow({
          lastActivatedMs,
          isFeed,
          isMultifamily,
        });

  const canAccessSFCostsAndFees = accessUtils.canAccessSFCostsAndFees({
    isFeed,
    isMultifamily,
  });

  const canAccessRelistingFlow = isInMultiplexListingFlow ? false : Boolean(listingDetailsData?.canBeRelisted);

  const canAccessFastPost = isInMultiplexListingFlow
    ? false
    : accessUtils.canAccessFastPost({
        isMultifamily,
        lastActivatedMs,
        isFeed,
        restrictions: listingDetailsData?.restrictions ?? { applications: { isAccessRestricted: false } },
      });

  const canAccessPostingPathTourScheduling =
    !isInMultiplexListingFlow &&
    accessUtils.canAccessPostingPathTourScheduling({
      isFeed,
      isInReactivationFlow: canAccessMLReactivationFlow,
      canAccessRelisting: canAccessRelistingFlow,
      active,
    });

  const canAccessScreeningCriteria = listingDetailsData?.listingCriteria?.isEnabled ?? false;

  const [hasOptedOutOfAi, setHasOptedOutOfAi] = useState(false);
  const isAiOptedOut = hasOptedOutOfAi || isAiOptOutStep(listingDetailsData?.modularListingProgress?.step);

  // Opting out of the AI flow leaves Test B only. The condensed flow carries no AI, so an opt-out
  // lands there rather than in the longer standard flow a non-enrolled landlord would never see.
  const canAccessCondensedFlow = isInMultiplexListingFlow
    ? false
    : accessUtils.canAccessCondensedFlow({
        canAccessRelistingFlow,
        canAccessMLReactivationFlow,
        lastActivatedMs,
      });

  // Shared with the steps so the lock and the flow read one source of truth. The hook resolves
  // eligibility before the Split call, which counts as an exposure.
  const { isEnabled: isAiListingCreationTestBEnrolled, isLoading: isAiListingTreatmentLoading } =
    useAiListingCreationTestBAccess();

  const canAccessAIListingCreationTestB = !isAiOptedOut && isAiListingCreationTestBEnrolled;

  const canAccessTotalMonthlyPriceWithFee = isInMultiplexListingFlow
    ? false
    : accessUtils.canAccessTotalMonthlyPriceWithFee({
        isEligible: listingDetailsData?.totalMonthlyPriceWithFeeNotice?.isEligible ?? false,
      });

  // Inside the loading gate below, because the Review CTA's label depends on this answer. Costs
  // nothing when verification cannot apply, since the query is disabled then.
  const { shouldShowVerificationStep: canAccessOwnershipVerification, isLoading: isOwnershipVerificationLoading } =
    useOwnershipVerificationAccess({ listingAlias });

  const addressComponents = isInMultiplexListingFlow ? { ...listingDetailsData, unit: '' } : (listingDetailsData ?? {});

  const { formattedStreet } = formatAddress(addressComponents);

  useListingInitialDefaults({ listingAlias, listingDetails: listingDetailsData });

  // Build wizard context from property data and flags
  const context = useMemo(() => {
    if (!listingDetailsData) return null;
    return buildWizardContext(
      listingDetailsData,
      {
        canAccessMultiplexListingFlow: isInMultiplexListingFlow ?? false,
        canAccessPostingPathTourScheduling,
        canAccessRelistingFlow,
        canAccessFastPost,
        canAccessSFCostsAndFees,
        canAccessScreeningCriteria,
        canAccessCondensedFlow,
        canAccessAIListingCreationTestB,
        canAccessTotalMonthlyPriceWithFee,
        canAccessMLReactivationFlow,
        canAccessOwnershipVerification,
      },
      new URLSearchParams(router.asPath.split('?')[1] ?? ''),
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [
    listingDetailsData,
    isInMultiplexListingFlow,
    canAccessPostingPathTourScheduling,
    canAccessRelistingFlow,
    canAccessFastPost,
    canAccessSFCostsAndFees,
    canAccessTotalMonthlyPriceWithFee,
    canAccessScreeningCriteria,
    canAccessCondensedFlow,
    canAccessAIListingCreationTestB,
    canAccessMLReactivationFlow,
    canAccessOwnershipVerification,
  ]);

  const [currentStepKey, setCurrentStepKey] = useState('');
  const [isStepInitialized, setIsStepInitialized] = useState(false);

  // Compute active steps based on context and user selections
  const activeSteps = useMemo(() => {
    if (!context || !listingDetailsData) return [];
    return getActiveSteps(
      listingDetailsData,
      context.flags,
      context.flowType,
      { tourBookingMethod: selectedBookingMethodForTourScheduling },
      // A new alias clears initialization but not `currentStepKey`, which would carry its step over.
      isStepInitialized ? currentStepKey : undefined,
    );
  }, [listingDetailsData, context, selectedBookingMethodForTourScheduling, currentStepKey, isStepInitialized]);

  const deepLinkStepInConstants = useMemo(() => {
    const deepLinkStep = typeof router.query.step === 'string' ? router.query.step.toUpperCase() : undefined;
    return deepLinkStep ? STEP_KEYS[deepLinkStep as keyof typeof STEP_KEYS] : undefined;
  }, [router.query.step]);

  const [isInitialMultiplexPrefetchComplete, setIsInitialMultiplexPrefetchComplete] = useState(false);

  useEffect(() => {
    setIsStepInitialized(false);
    setIsInitialMultiplexPrefetchComplete(false);
    setHasOptedOutOfAi(false);
  }, [listingAlias]);

  // Validation error tracking
  const errorCount = useRef(0);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);

  const reportError = useCallback(() => {
    errorCount.current++;
    setHasValidationErrors(true);
  }, []);

  const clearError = useCallback(() => {
    errorCount.current = Math.max(0, errorCount.current - 1);
    setHasValidationErrors(errorCount.current > 0);
  }, []);

  useEffect(() => {
    if (
      !listingDetailsData ||
      !context ||
      activeSteps.length === 0 ||
      isStepInitialized ||
      isAiListingTreatmentLoading ||
      isOwnershipVerificationLoading
    ) {
      return;
    }

    const hasActiveWizardStep = Boolean(currentStepKey) && activeSteps.some((step) => step.stepKey === currentStepKey);

    // Unit switch / in-wizard navigation: listing details refetch only, not initial step resolve.
    if (hasActiveWizardStep) {
      setIsStepInitialized(true);
      return;
    }

    const savedStep = listingDetailsData?.modularListingProgress?.step;

    const resolved = resolveInitialStep({
      context,
      savedStep,
      deepLinkStep: deepLinkStepInConstants,
      activeSteps,
      isFeed,
      active,
    });

    setCurrentStepKey(resolved.stepKey);
    setIsStepInitialized(true);

    if (resolved.shouldSave) {
      saveStepProgress(resolved.stepKey);
    } else if (savedStep && !resolved.shouldSave && user) {
      // Track continue from if we're resuming from a saved step
      emitContinueFromEvent({
        currentProperty: listingDetailsData,
        user,
        currentStepKey: resolved.stepKey,
      });
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [
    listingDetailsData,
    context,
    activeSteps,
    isStepInitialized,
    isFeed,
    active,
    deepLinkStepInConstants,
    currentStepKey,
    isAiListingTreatmentLoading,
    isOwnershipVerificationLoading,
  ]);

  // Show clone success toast
  useEffect(() => {
    if (router.query.listingCloneConversionSuccess === 'true') {
      enqueueToast(<Toast tone="success">Listing created</Toast>);
    }
  }, [router.query.listingCloneConversionSuccess, enqueueToast]);

  useEffect(() => {
    if (listingDetailsData?.active) {
      emitActiveListingViewEvent({ currentProperty: listingDetailsData, user });
    } else if (listingDetailsData?.lastActivatedMs) {
      emitOffMarketListingViewEvent({ currentProperty: listingDetailsData, user });
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [listingDetailsData?.active, listingDetailsData?.lastActivatedMs]);

  // Get progress groups for current flow, filtered to only those with active steps
  const progressGroups = useMemo(() => {
    if (!context) return [];
    return getActiveProgressGroups(activeSteps, context.flowType);
  }, [context, activeSteps]);

  // Multiplex step prefetch: warm React Query cache before the wizard renders on first entry only.
  // In-wizard navigation (goToStep, unit switch with an active step) skips prefetch; step components load themselves.
  const shouldPrefetchMultiplexStep = isInMultiplexListingFlow === true && !isInitialMultiplexPrefetchComplete;

  // Which multiplex query to prefetch (details, overview, or unit published). Same resolution as step init on first load.
  const targetStepKey = useMemo(() => {
    if (!shouldPrefetchMultiplexStep || !listingDetailsData || !context || activeSteps.length === 0) {
      return null;
    }

    const savedStep = listingDetailsData.modularListingProgress?.step;

    return resolveInitialStep({
      context,
      savedStep,
      deepLinkStep: deepLinkStepInConstants,
      activeSteps,
      isFeed,
      active,
    }).stepKey;
  }, [
    // Recompute when prefetch eligibility ends so targetStepKey becomes null (other deps may not change).
    shouldPrefetchMultiplexStep,
    listingDetailsData,
    context,
    activeSteps,
    isFeed,
    active,
    deepLinkStepInConstants,
  ]);

  const { error: errorMultiplexStepPrefetch, isPrefetching: isMultiplexStepPrefetching } = useMultiplexStepPrefetch({
    targetStepKey,
    enabled: shouldPrefetchMultiplexStep,
  });

  useEffect(() => {
    if (shouldPrefetchMultiplexStep && targetStepKey && !isMultiplexStepPrefetching && !errorMultiplexStepPrefetch) {
      setIsInitialMultiplexPrefetchComplete(true);
    }
  }, [shouldPrefetchMultiplexStep, targetStepKey, isMultiplexStepPrefetching, errorMultiplexStepPrefetch]);

  const currentIndex = useMemo(() => {
    return activeSteps.findIndex((s) => s.stepKey === currentStepKey);
  }, [activeSteps, currentStepKey]);

  const { hasAIFields } = useAiGeneratedFields();

  const hasLeftAiListingFirstStep =
    context?.flowType === FlowType.AI_LISTING_CREATION_TEST_B &&
    currentStepKey !== STEP_KEYS.AI_LISTING_PHOTOS_AND_NOTES &&
    hasAIFields;

  // Compute wizard title from current step's progress group (matches legacy behavior)
  const wizardTitle = useMemo(() => {
    const currentStep = activeSteps[currentIndex];
    if (!currentStep) return '';

    // Find the progress group label for the current step
    const progressGroup = progressGroups.find((pg) => pg.key === currentStep.progressStepKey);
    return progressGroup?.label ?? '';
  }, [activeSteps, currentIndex, progressGroups]);

  const saveStepProgress = async (
    stepKey: string,
    listingAliasOverride?: string,
    { isOptedOut = isAiOptedOut }: { isOptedOut?: boolean } = {},
  ): Promise<void> => {
    // Feed listings are not in modular listing progress; the API has no record to update.
    if (isFeed) {
      return;
    }

    let step = stepKey.toLowerCase();

    if (canAccessRelistingFlow && stepKey === STEP_KEYS.REVIEW) {
      step = `${step}${STEP_DELIMITER}${RELISTING_STEP_SUFFIX}`;
    } else if (isOptedOut) {
      step = `${step}${STEP_DELIMITER}${AI_OPT_OUT_STEP_SUFFIX}`;
    }

    const aliasForSave = listingAliasOverride ?? listingAlias;
    await saveWizardProgress({ postData: { listingAlias: aliasForSave, step } });
  };

  // Navigation handlers
  const goToStep = (
    stepKey: string,
    { stepDirection = '', listingAlias: listingAliasOverride }: GoToStepOptions = {},
  ) => {
    window.scrollTo(0, 0);

    if (stepDirection === STEP_DIRECTION.NEXT) {
      emitNextEvent({ currentProperty: listingDetailsData, user, currentStepKey: stepKey });
    }

    saveStepProgress(stepKey, listingAliasOverride).catch((error) => {
      console.error('Failed to save progress:', error);
    });

    setCurrentStepKey(stepKey);
  };

  // Leaving the AI flow changes the step order, and the step-resolve effect only runs once, so the
  // first step of the condensed flow is set here rather than left to re-resolve.
  const optOutOfAiListing = () => {
    window.scrollTo(0, 0);
    setHasOptedOutOfAi(true);

    // Property info opens the condensed flow, and a landlord in Test B always satisfies its
    // screening-criteria guard, so this is the first step for anyone who can opt out.
    setCurrentStepKey(STEP_KEYS.PROPERTY_INFO);

    saveStepProgress(STEP_KEYS.PROPERTY_INFO, undefined, { isOptedOut: true }).catch((error) => {
      console.error('Failed to save progress:', error);
    });
  };

  const exit = async () => {
    try {
      if (
        canAccessTotalMonthlyPriceWithFee &&
        listingDetailsData?.active &&
        !listingDetailsData?.displayTotalMonthlyPriceWithFee
      ) {
        await setDisplayTotalMonthlyPriceWithFee({
          postData: { listingAlias, displayTotalMonthlyPriceWithFee: true },
        });
      }
    } finally {
      queryClient.invalidateQueries({ queryKey: ['getListingSummary', listingAlias] });

      const isMultiplexPropertyDetailsOrOverviewStep =
        currentStepKey === STEP_KEYS.MULTIPLEX_PROPERTY_DETAILS || currentStepKey === STEP_KEYS.MULTIPLEX_OVERVIEW;

      if (getPortfolioOrigin() === PORTFOLIO_ORIGIN.listings) {
        router.push(getListingsReturnRoute());
      } else {
        router.push(
          isMultiplexPropertyDetailsOrOverviewStep ? ROUTES.PROPERTIES : getDashboardOverviewRoute({ listingAlias }),
        );
      }
    }
  };

  const handleExitClick = async () => {
    if (isFeed) {
      await exit();
      return;
    }

    const isOnReviewStep = currentStepKey === STEP_KEYS.REVIEW || currentStepKey === STEP_KEYS.REVIEW_AI;

    // Only track save & exit when not coming from SaveAndExitModal
    if (!isOnReviewStep || listingDetailsData?.active) {
      emitSaveAndExitEvent({ currentProperty: listingDetailsData, user, currentStepKey });
    }
    await saveStepProgress(currentStepKey);
    await exit();
  };

  if (errorMultiplexStepPrefetch || listingDetailsError || modularListingProgressError) {
    const httpStatus = (
      (errorMultiplexStepPrefetch || listingDetailsError || modularListingProgressError) as DatarouterError
    ).responseStatus;

    if (errorMultiplexStepPrefetch) {
      return <ErrorPage type={ERROR_PAGE_TYPES.MULTIPLEX_PROPERTY_LOAD_ERROR} />;
    }

    if (httpStatus === 400) {
      return <ErrorPage type={ERROR_PAGE_TYPES.NOT_FOUND} />;
    }

    if (httpStatus === 403) {
      return <ErrorPage type={ERROR_PAGE_TYPES.UNAUTHORIZED} />;
    }

    return <ErrorPage type={ERROR_PAGE_TYPES.TECHNICAL_DIFFICULTIES} />;
  }

  const isMultiplexFlowSettling = Boolean(multiplexBuildingSessionId) && isMultiplexListingFlowLoading;

  if (
    isLoadingProperty ||
    isAiListingTreatmentLoading ||
    isOwnershipVerificationLoading ||
    isMultiplexFlowSettling ||
    isMultiplexStepPrefetching ||
    !listingDetailsData ||
    !context ||
    !isStepInitialized ||
    !currentStepKey
  ) {
    return <CLPLoadingState isNewProperty={isNewProperty} isZram={user?.isEnterpriseCustomer} />;
  }

  return (
    <>
      <Head>
        <title>Edit {formattedStreet || 'listing'} | Zillow Rental Manager</title>
      </Head>
      <TourSchedulingProvider
        listingAlias={listingAlias}
        canAccessPostingPathTourScheduling={canAccessPostingPathTourScheduling}
        selectedBookingMethod={selectedBookingMethodForTourScheduling}
        setSelectedBookingMethod={setSelectedBookingMethodForTourScheduling}
      >
        <WizardNavigationProvider
          value={{
            goToStep,
            currentStepKey,
            reportError,
            clearError,
            optOutOfAiListing,
          }}
        >
          <AiDraftGenerationProvider
            listingAlias={listingAlias}
            groups={[
              ...(listingDetailsData?.amenities?.interior ?? []),
              ...(listingDetailsData?.amenities?.property ?? []),
            ]}
          >
            {!isAiOptedOut && <AiDraftFailureGate />}
            <WizardStepController
              currentStepKey={currentStepKey}
              steps={activeSteps}
              progressSteps={progressGroups}
              onGoToStep={goToStep}
              wizardTitle={wizardTitle}
              onExitWizard={handleExitClick}
              isBackDisabled={currentIndex === 0}
              isNextDisabled={hasValidationErrors}
              shouldRenderProgressStepper={true}
              listingDetails={listingDetailsData}
              exitCTALabel={EXIT_CTA_LABEL.SAVE_AND_EXIT}
              labelAppearanceBreakpoint="xl"
              displayPremiumTopOfFunnelHeader={true}
              allowInteractivity={true}
              nonInteractiveProgressStepKeys={[
                PROGRESS_STEP_KEYS.PAY_AND_PUBLISH,
                PROGRESS_STEP_KEYS.PUBLISHED,
                PROGRESS_STEP_KEYS.PROPERTY_DETAILS,
                PROGRESS_STEP_KEYS.VERIFY_OWNERSHIP,
                ...(isInMultiplexListingFlow ? [PROGRESS_STEP_KEYS.REVIEW] : []),
                ...(hasLeftAiListingFirstStep ? [PROGRESS_STEP_KEYS.AI_LISTING_PHOTOS_AND_NOTES] : []),
              ]}
            />
          </AiDraftGenerationProvider>
        </WizardNavigationProvider>
      </TourSchedulingProvider>
    </>
  );
}
